import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react";
import type { Language, User } from "../shared/contracts";
import { clearSignupDrafts, completeSignupLogin } from "./lib/brand-web";
import {
  authGeneration,
  beginAuthRefresh,
  invalidateAuth,
  invalidateAuthIdentity,
  isCurrentAuth,
} from "./lib/auth-lifecycle";
import { api, ApiFailure, requestLanguage } from "./api";
type ContextValue = {
  user: User | null;
  loading: boolean;
  demo: boolean;
  language: Language;
  t: (ja: string, zh: string) => string;
  setLanguage: (language: Language) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  toast: (text: string) => void;
  errorText: (error: unknown) => string;
};
const Context = createContext<ContextValue>(null!);
export function Provider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null),
    [loading, setLoading] = useState(true),
    [demo, setDemo] = useState(false),
    [language, setLang] = useState<Language>(requestLanguage),
    [notice, setNotice] = useState("");
  const identity = useRef<string | null>(null);
  function updateUser(next: User | null) {
    const nextId = next?.id ?? null;
    if (identity.current !== nextId) invalidateAuthIdentity();
    identity.current = nextId;
    setUser(next);
  }
  async function refresh() {
    const ticket = beginAuthRefresh();
    if (ticket === null) return;
    const data = await api<{ user: User | null }>("/auth/me");
    if (!isCurrentAuth(ticket)) return;
    if (data.user) completeSignupLogin(sessionStorage, data.user.id);
    updateUser(data.user);
  }
  useEffect(() => {
    Promise.all([
      refresh(),
      api<{ demo: boolean }>("/meta").then((d) => setDemo(d.demo)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    const foreground = () => {
      if (document.visibilityState === "visible") refresh().catch(() => {});
    };
    const lost = () => {
      invalidateAuth();
      clearSignupDrafts(sessionStorage);
      updateUser(null);
    };
    window.addEventListener("focus", foreground);
    document.addEventListener("visibilitychange", foreground);
    window.addEventListener("juyici:auth-lost", lost);
    return () => {
      window.removeEventListener("focus", foreground);
      document.removeEventListener("visibilitychange", foreground);
      window.removeEventListener("juyici:auth-lost", lost);
    };
  }, []);
  useEffect(() => {
    window.dispatchEvent(new Event("juyici:identity"));
  }, [user?.id]);
  useEffect(() => {
    document.documentElement.lang = language === "ja" ? "ja" : "zh-CN";
    localStorage.setItem("juyici_language", language);
  }, [language]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 5500);
    return () => clearTimeout(timer);
  }, [notice]);
  const t = (ja: string, zh: string) => (language === "ja" ? ja : zh);
  function errorText(error: unknown) {
    if (error instanceof ApiFailure) return error.message;
    return t("接続できません。もう一度お試しください。", "连接失败，请重试。");
  }
  return (
    <Context.Provider
      value={{
        user,
        loading,
        demo,
        language,
        t,
        setLanguage: setLang,
        refresh,
        logout: async () => {
          // Invalidate identity work at start, but keep the login UI unavailable
          // until the clearing cookie response completes (Layout awaits logout).
          const pending = api("/auth/logout", "POST");
          const ticket = authGeneration();
          await pending;
          if (!isCurrentAuth(ticket)) return;
          clearSignupDrafts(sessionStorage);
          updateUser(null);
        },
        toast: setNotice,
        errorText,
      }}
    >
      <Fragment key={user?.id ?? "anonymous"}>{children}</Fragment>
      {notice && (
        <div className="toast" role="status">
          {notice}
        </div>
      )}
    </Context.Provider>
  );
}
export const useApp = () => useContext(Context);
